import { DeviceTypes } from "../../config/documents/HardwareConfig";
import { PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import PoolDevice from "./base";
import { DisplayProcessingLogics, StatesToDisplayItem } from "../Types";
import { poolsideEquipmentTypes, ValueAndUnit } from "../../types";
import logger from "../../utils/Logger";
import * as Alert from "../../alert";

export default class PoolDeviceDoorSensor extends PoolDevice implements PoolDeviceInterface {
    static HideFromPicker = true;
    static DeviceType = DeviceTypes.DOORSENSOR;
    static Icon = "sensor-door";
    static PickerTitle = Locale.gettext("Door Sensor");


    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "Door Sensor": return Locale.gettext("Door Sensor");    
        case "Door": return Locale.gettext("Door");
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.OPEN_CLOSE:
            /* Uncomment for mocking state value
            value = JSON.stringify({
                "1": false, // Module 1: Door closed,
                "2": true, // Module 2: Door opened,
                "3": true, // Module 3: Door opened,
                "4": true, // Module 4: Door opened,
                "12-12-34-245-546": true, // Module 12-12-34-245-546: Door opened,
            });
            */
            
            // If AutoFilterVersion < 28
            // or it is a dual attendant setup and the primary Controller alredy reports v28
            // but the secondary still running old version so it does not have the new object format.
            switch(value) {
            case "CLOSED": return { value: Locale.gettext("Closed") };
            case "OPEN": return { value: Locale.gettext("Open") };
            default: {
                const platformCapabilities = JSON.parse(this.siteStates?.PlatformCapabilities || "{}");
                const platformCapabilitiesControllerModules = platformCapabilities
                    ?.Controllers
                    ?.find(item => item.ControllerUUID === this.UUID)
                    ?.Modules
                ;
                    
                let valueDecoded;
                try {
                    valueDecoded = JSON.parse(value || "{}")
                } catch (err) {
                    logger.info("Could not decode door sensor value: ", value);
                    return { value };
                }
                
                const moduleDoors = Object.entries(valueDecoded || {})
                    // Only show door status of Modules that have Type: "POOLSIDE" in PlatformCapabilities
                    .filter(([ key, value ]) =>
                        Object.values(poolsideEquipmentTypes).includes(
                            platformCapabilitiesControllerModules
                                ?.find(item => item.ModuleNumber === key)
                                ?.Type
                        )
                    );
                
                const ret = [];
                // Only prefix open/close status by "Module X:" in case there are more than 1 modules
                moduleDoors.forEach(([ key, value ]) => {
                    ret.push(Locale.gettext("%s%s",
                        moduleDoors.length > 1 ? Locale.gettext("Module %s:", key) : "",
                        value ?
                            Locale.gettext("Open") :
                            Locale.gettext("Closed")
                    ));
                });
                return { value: ret.join("\n") };
            } }

           
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    getAlerts(): Alert.AlertInfo[] {
        const allConfig = this.allConfigs;
        const alertInfo = [];
        const alertsSource = this.allAlerts?.filter(item => item.entityUuid?.includes(this.UUID)) || [];
        const alerts = JSON.parse(JSON.stringify(alertsSource)) as Alert.AlertRecord[];
        alerts.forEach(itemAlerts => {
            alertInfo.push(Alert.getInfo(itemAlerts, allConfig, this.allStates));
        });

        return alertInfo;
    }

    getMainMetricField(): StatesToDisplayItem {
        return ({
            Name: PoolsideDaemonDeviceStateNames.DoorSensor,
            DisplayName: "Door Sensor",
            DisplayNameShort: "Door",
            DisplayOrder: 1,
            DisplayProcessingLogic: DisplayProcessingLogics.OPEN_CLOSE,
            DisplayProcessingLogicUnitAsDisplayName: false
        });
    }    
}

export enum PoolsideDaemonDeviceStateNames {
    DoorSensor = "DoorSensor",
}
